import React, { useCallback, useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { APIfetchPlans, APIopenBillingPortal, APIselectPlan } from '../../api/Plan';
import { useStore } from '../../store';
import type { EmbeddedCheckoutSession, PlanKey, PlanSummary } from '../../api/Plan';
import Checkout from './Checkout';

const DashboardPlan: React.FC = () => {
  const routeApi = getRouteApi('/dashboard/plan');
  const { plans: initialPlans }: { plans: PlanSummary } = routeApi.useLoaderData();
  const userId = useStore((state) => state.profile?.id);
  const { checkout } = routeApi.useSearch();
  const [interval, setInterval] = useState<'month' | 'year'>(
    initialPlans.prices.find((price) => price.key === initialPlans.price_key)?.interval ?? 'month',
  );
  const [refreshUntil, setRefreshUntil] = useState(() => checkout === 'success' ? Date.now() + 60_000 : 0);
  const [checkoutSession, setCheckoutSession] = useState<EmbeddedCheckoutSession | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const { data: plans, error: loadError, refetch } = useQuery<PlanSummary>({
    queryKey: ['plans', userId],
    queryFn: APIfetchPlans,
    initialData: initialPlans,
    refetchInterval: () => Date.now() < refreshUntil ? 3000 : false,
  });
  const dismissCheckout = useCallback(() => {
    setCheckoutSession(null);
    void refetch();
  }, [refetch]);
  const completeCheckout = useCallback(() => {
    setCheckoutSession(null);
    setMessage('Checkout completed. Your plan updates after payment is confirmed.');
    setRefreshUntil(Date.now() + 60_000);
    void refetch();
  }, [refetch]);
  const { mutate: selectPlan, isPending, error } = useMutation({
    mutationFn: APIselectPlan,
    onSuccess: (result) => {
      if (result.checkout) {
        setCheckoutSession(result.checkout);
      } else if (result.url) {
        window.location.assign(result.url);
      } else {
        setMessage(result.message ?? null);
        void refetch();
      }
    },
  });
  const { mutate: openPortal, isPending: openingPortal, error: portalError } = useMutation({
    mutationFn: APIopenBillingPortal,
    onSuccess: (result) => window.location.assign(result.url),
  });
  const errorMessage = (failure: unknown): string => {
    if (isAxiosError<{ message: string; errors?: Record<string, string[]> }>(failure)) {
      const errors = failure.response?.data.errors;
      return errors ? Object.values(errors).flat().join(' ') : failure.response?.data.message ?? 'Unable to reach billing. Please try again.';
    }
    return 'Unable to load billing details. Please try again.';
  };
  const priceFor = (plan: PlanKey) => plans.prices.find((price) => price.plan_key === plan && price.interval === interval);
  const formatPrice = (plan: PlanKey) => {
    const price = priceFor(plan);
    return price ? new Intl.NumberFormat('en-US', { style: 'currency', currency: price.currency }).format(price.amount / 100) : 'Unavailable';
  };
  const isCurrent = (plan: PlanKey) => plan === 'free'
    ? plans.plan_key === 'free' && !plans.price_key
    : plans.price_key === priceFor(plan)?.key;
  const select = (plan: PlanKey) => {
    const key = plan === 'free' ? 'free' : priceFor(plan)?.key;
    if (key) {
      setMessage(null);
      selectPlan(key);
    }
  };
  const buttonLabel = (plan: PlanKey, name: string) => {
    if (isPending) return 'Processing...';
    if (isCurrent(plan)) return plans.pending_plan_key ? 'Keep current plan' : 'Current plan';
    return plan === 'free' ? 'Switch to Free at renewal' : `Pick ${name} plan`;
  };
  const disabled = (plan: PlanKey) => isPending || openingPortal || checkoutSession !== null
    || (plan !== 'free' && !priceFor(plan))
    || (isCurrent(plan) && !plans.pending_plan_key);

  if (checkoutSession) {
    return (
      <>
        <div className="dashboard-header page-header">
          <h2 className="page-title">Complete your <em>subscription</em></h2>
          <p>Secure payment powered by Stripe.</p>
        </div>
        <Checkout session={checkoutSession} onComplete={completeCheckout} onDismiss={dismissCheckout} />
      </>
    );
  }

  return (
    <>
      <div className="dashboard-header page-header">
        <h2 className="page-title">My <em>plan</em></h2>
        <p>Manage your current plan and view its details.</p>
      </div>
      <div className="inner wa-stack wa-gap-s">
        <p>Upgrades are charged immediately with proration. Downgrades take effect at your next renewal.</p>
        <p>Current plan: <strong>{plans.plan_key === 'free' ? 'Free' : `LGCY ${plans.plan_key}`}</strong></p>
        {plans.subscription_status && (
          <wa-button appearance="outlined" disabled={isPending || openingPortal} onClick={() => openPortal()}>
            {openingPortal ? 'Opening billing...' : 'Manage payment methods and invoices'}
          </wa-button>
        )}
        {plans.subscription_status && ['incomplete', 'past_due', 'unpaid'].includes(plans.subscription_status) && (
          <wa-callout variant="warning">Your subscription payment needs attention. Manage your payment methods and invoices before changing plans.</wa-callout>
        )}
        {checkout === 'success' && (
          <wa-callout variant="neutral">You have returned from Stripe. Your plan updates after payment is confirmed. <wa-button size="s" appearance="plain" onClick={() => void refetch()}>Refresh status</wa-button></wa-callout>
        )}
        {checkout === 'cancelled' && <wa-callout variant="neutral">Checkout was cancelled. Your plan has not changed.</wa-callout>}
        {plans.pending_plan_key && plans.pending_change_at && (
          <wa-callout variant="neutral">Your plan will change to {plans.pending_plan_key} on {new Date(plans.pending_change_at).toLocaleDateString()}. Choose your current plan to cancel this change.</wa-callout>
        )}
        {message && <wa-callout variant="success" role="status">{message}</wa-callout>}
        {(error || portalError || loadError) && <wa-callout variant="danger" role="alert">{errorMessage(error ?? portalError ?? loadError)}</wa-callout>}
      </div>
      <div className="dashboard-plan-period-switch wa-cluster wa-gap-s" role="group" aria-label="Billing period">
        <wa-button appearance={interval === 'month' ? 'filled' : 'outlined'} aria-pressed={interval === 'month' ? 'true' : 'false'} disabled={isPending} onClick={() => setInterval('month')}>
          {interval === 'month' && <i className="fa-solid fa-check" slot="start" aria-hidden="true" />}
          Monthly
        </wa-button>
        <wa-button appearance={interval === 'year' ? 'filled' : 'outlined'} aria-pressed={interval === 'year' ? 'true' : 'false'} disabled={isPending} onClick={() => setInterval('year')}>
          {interval === 'year' && <i className="fa-solid fa-check" slot="start" aria-hidden="true" />}
          Annual
        </wa-button>
      </div>
      <div className="dashboard-plan wa-grid wa-gap-s inner">
        <wa-card className="dashboard-plan-card dashboard-plan-card-free" data-current-plan={plans.plan_key === 'free' ? 'true' : 'false'}>
          <div className="wa-stack wa-gap-2xs">
            <div className="page-eyebrow">Free - right now, everything</div>
            <div className="dashboard-plan-price wa-cluster wa-gap-xs">
              <h2>$0</h2>
              <span>right now, everything</span>
            </div>
            <p>
              Sprk unlimited memories, build timelines, and share freely.
              No credit card, no catch.
            </p>
            <ul className="plan-features">
              <li>Unlimited memories</li>
              <li>Up to 5 GB of media</li>
              <li>Share via link or up to 5 direct email</li>
              <li>React & comment on shared memories</li>
              <li>Share via social channels</li>
              <li>LGCY Card available · $29.95</li>
            </ul>
            <wa-button
              appearance="outlined"
              disabled={disabled('free')}
              onClick={() => select('free')}
            >
              {buttonLabel('free', 'Free')}
            </wa-button>
          </div>
        </wa-card>
        <wa-card className="dashboard-plan-card dashboard-plan-card-lite" data-current-plan={plans.plan_key === 'lite' ? 'true' : 'false'}>
          <div className="wa-stack wa-gap-2xs">
            <div className="page-eyebrow">LGCY Lite</div>
            <div className="dashboard-plan-price wa-cluster wa-gap-xs">
              <h2>{formatPrice('lite')}</h2>
              <span>/ {interval}</span>
            </div>
            <p>
              Start building a timeline. The first step toward
              preserving a full story.
            </p>
            <ul className="plan-features">
              <li>1 active LGCY Timeline</li>
              <li>Up to 10 GB of media</li>
              <li>Share via link or up to 20 direct email</li>
              <li>Share via social channels</li>
              <li>LGCY Card available · $29.95 or sign up for year Card is Free</li>
            </ul>
            <wa-button
              appearance="outlined"
              disabled={disabled('lite')}
              onClick={() => select('lite')}
            >
              {buttonLabel('lite', 'LGCY Lite')}
            </wa-button>
          </div>
        </wa-card>
        <wa-card className="dashboard-plan-card dashboard-plan-card-standard" data-current-plan={plans.plan_key === 'standard' ? 'true' : 'false'} data-promoted={plans.plan_key === 'free' ? 'true' : 'false'}>
          <div className="wa-stack wa-gap-2xs">
            {plans.plan_key === 'free' && <span className="dashboard-plan-popular">Most popular</span>}
            <div className="page-eyebrow">LGCY Standard</div>
            <div className="dashboard-plan-price wa-cluster wa-gap-xs">
              <h2>{formatPrice('standard')}</h2>
              <span>/ {interval}</span>
            </div>
            <p>
              The full storytelling experience. Video, audio,
              and powerful AI tools.
            </p>
            <ul className="plan-features">
              <li>3 active LGCY Timeline</li>
              <li>Up to 50 GB of media</li>
              <li>Full video & audio support</li>
              <li>Timeline chapters & themes</li>
              <li>Share via link or up to 25 direct email</li>
            </ul>
            <wa-button
              appearance="outlined"
              disabled={disabled('standard')}
              onClick={() => select('standard')}
            >
              {buttonLabel('standard', 'LGCY Standard')}
            </wa-button>
          </div>
        </wa-card>
        <wa-card className="dashboard-plan-card dashboard-plan-card-forever" data-current-plan={plans.plan_key === 'forever' ? 'true' : 'false'}>
          <div className="wa-stack wa-gap-2xs">
            <div className="page-eyebrow">LGCY Forever</div>
            <div className="dashboard-plan-price wa-cluster wa-gap-xs">
              <h2>{formatPrice('forever')}</h2>
              <span>/ {interval}</span>
            </div>
            <p>
              Unlimited everything. For families preserving a full legacy.
            </p>
            <ul className="plan-features">
              <li>Unlimited active LGCY Timeline</li>
              <li>Unlimited media uploads</li>
              <li>Full video & audio support</li>
              <li>Timeline chapters & themes</li>
              <li>Priority support</li>
              <li>Unlimited email sharing</li>
              <li>One LGCY Card included FREE</li>
            </ul>
            <wa-button
              appearance="outlined"
              disabled={disabled('forever')}
              onClick={() => select('forever')}
            >
              {buttonLabel('forever', 'LGCY Forever')}
            </wa-button>
          </div>
        </wa-card>
      </div>
    </>
  );
}

export default DashboardPlan;
